import { ISO6391Code, ISO15924ScriptCode } from './types';
import {
  languageScriptsMap as rawLanguageScriptsMap,
  SCRIPT_GROUPS as rawScriptGroups,
} from './data';

// Make the data structures truly readonly
export const languageScriptsMap = Object.freeze(rawLanguageScriptsMap);
export const SCRIPT_GROUPS = Object.freeze(rawScriptGroups);

/**
 * Gets the default script code for a given language code
 * @param languageCode - ISO 639-1 language code (case-insensitive)
 * @returns The ISO 15924 script code for the language, or undefined if not found
 * @example
 * getScriptByLanguageCode('en') // returns 'Latn'
 * getScriptByLanguageCode('ar') // returns 'Arab'
 * getScriptByLanguageCode('invalid') // returns undefined
 */
export function getScriptByLanguageCode(
  languageCode: ISO6391Code | string
): ISO15924ScriptCode | undefined {
  if (!languageCode) return undefined;
  return languageScriptsMap[languageCode.toLowerCase()];
}

/**
 * Gets all language codes that use a given script
 * @param scriptCode - ISO 15924 script code (case-insensitive)
 * @returns Array of ISO 639-1 language codes that use the script
 * @example
 * getLanguagesByScript('Latn') // returns ['en', 'fr', 'de', ...]
 * getLanguagesByScript('Arab') // returns ['ar', 'fa', 'ur', ...]
 * getLanguagesByScript('invalid') // returns []
 */
export function getLanguagesByScript(scriptCode: ISO15924ScriptCode | string): ISO6391Code[] {
  if (!scriptCode) return [];
  const normalizedScript = scriptCode.toUpperCase();
  return Object.entries(languageScriptsMap)
    .filter(([_, script]) => script.toUpperCase() === normalizedScript)
    .map(([lang]) => lang);
}

/**
 * Checks if a given script code is valid (exists in the dataset)
 * @param scriptInput - ISO 15924 script code to check (case-insensitive)
 * @returns true if the script code exists in the dataset
 * @example
 * isValidScript('Latn') // returns true
 * isValidScript('Arab') // returns true
 * isValidScript('invalid') // returns false
 */
export function isValidScript(scriptInput: string): boolean {
  if (!scriptInput) return false;
  const normalizedScript = scriptInput.toUpperCase();
  return Object.values(languageScriptsMap).some(
    (script) => script.toUpperCase() === normalizedScript
  );
}

/**
 * Checks if a given script is the default script for a language
 * @param languageCode - ISO 639-1 language code (case-insensitive)
 * @param scriptInput - ISO 15924 script code to check (case-insensitive)
 * @returns true if the script is the default for the language
 * @example
 * isScriptMatch('en', 'Latn') // returns true
 * isScriptMatch('ar', 'Arab') // returns true
 * isScriptMatch('en', 'Arab') // returns false
 */
export function isScriptMatch(
  languageCode: ISO6391Code | string,
  scriptInput: ISO15924ScriptCode | string
): boolean {
  if (!languageCode || !scriptInput) return false;
  const langScript = getScriptByLanguageCode(languageCode);
  return langScript?.toUpperCase() === scriptInput.toUpperCase();
}

// Export types
export * from './types';
